import { useMemo } from "react";
import {
  parseActorPlace,
  parseHeldProp,
  StateValue,
} from "../../helpers/SequenceStateAtNode";
import InteractionPointPreview, {
  CAMERA_MARKER_CLASS,
  CAMERA_MARKER_Z_INDEX_OFFSET,
  SelectedCameraPointStrategy,
} from "./InteractionPointPreview";
import SequenceAttachmentMarker, { AttachmentMember } from "./SequenceAttachmentMarker";
import { sequenceStateDotClassName } from "./sequenceStateDot";
import useAttachmentMarkerParts from "./useAttachmentMarkerParts";
import useSequenceStateOnMap from "./useSequenceStateOnMap";

interface DockedAttachment {
  key: string;
  positionId: string;
  point: { x: number; y: number };
  host: AttachmentMember;
  guests: AttachmentMember[];
  names: string[];
  hostName: string;
  beside: boolean;
  changed: boolean;
  uncertain: boolean;
}

/**
 * What the sequence state adds on the map besides where actors stand (the
 * actor layers place those, see useSequenceActorPlacements):
 * - attachments docked on their host: a prop in an actor's hand (Prop to Player) on the
 *   actor's spot, an actor on a prop (Player to Prop) on the prop's spot;
 * - the camera a Set Camera node left active.
 */
export default function Viewport3DSequenceStateLayer() {
  const focus = useSequenceStateOnMap();
  const {
    points,
    takeColor,
    takeColorRgba,
    actorName,
    propName,
    actorMember,
    propMember,
    placementOf,
  } =
    useAttachmentMarkerParts(focus?.take);

  const attachments = useMemo(() => {
    if (!focus) return [];
    const { state } = focus;
    const byKey = new Map<string, DockedAttachment>();
    const add = (
      key: string,
      positionId: string | undefined,
      host: AttachmentMember,
      hostName: string,
      guest: AttachmentMember,
      guestName: string,
      values: StateValue[],
      beside = false,
    ) => {
      const point = positionId ? points.get(positionId) : undefined;
      if (!point) return;
      const existing = byKey.get(key);
      const changed = values.some((value) => value.changed);
      const uncertain = values.some((value) => value.values.length > 1);
      if (existing) {
        existing.guests.push(guest);
        existing.names.push(guestName);
        existing.changed ||= changed;
        existing.uncertain ||= uncertain;
        return;
      }
      byKey.set(key, {
        key,
        positionId: positionId!,
        point,
        host,
        guests: [guest],
        names: [guestName],
        hostName,
        beside,
        changed,
        uncertain,
      });
    };

    // Prop to Player: the prop is in the hand, wherever the actor stands.
    Object.entries(state.heldProps).forEach(([propId, heldValue]) =>
      heldValue.values.map(parseHeldProp).forEach((held) => {
        if (held.kind !== "heldBy") return;
        const actorValue = state.actors[held.actorId];
        (actorValue?.values ?? []).map(parseActorPlace).forEach((place) => {
          if (place.kind !== "position") return;
          add(
            `held:${held.actorId}:${place.positionId}`,
            place.positionId,
            actorMember(held.actorId),
            actorName(held.actorId),
            propMember(propId),
            propName(propId),
            [heldValue, actorValue],
          );
        });
      }),
    );

    // Player to Prop: the actor is on the prop, where the prop is placed.
    Object.entries(state.actors).forEach(([actorId, actorValue]) =>
      actorValue.values.map(parseActorPlace).forEach((place) => {
        if (place.kind === "position") return;
        add(
          `${place.kind}:${place.propId}`,
          placementOf(place.propId),
          propMember(place.propId),
          propName(place.propId),
          actorMember(actorId),
          actorName(actorId),
          [actorValue],
          place.kind === "beside",
        );
      }),
    );

    return [...byKey.values()];
  // The marker parts are rebuilt with the take; following focus is enough.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus, points]);

  if (!focus) return null;
  const camera = focus.state.camera;

  return (
    <>
      {attachments.map((attachment) => (
        <SequenceAttachmentMarker
          key={attachment.key}
          x={attachment.point.x}
          y={attachment.point.y}
          host={attachment.host}
          guests={attachment.guests}
          held={attachment.key.startsWith("held:")}
          beside={attachment.beside}
          takeColor={takeColor}
          isTarget={
            !!focus.targetId &&
            (attachment.positionId === focus.targetId ||
              attachment.positionId.split("/").pop() === focus.targetId)
          }
          dot={attachment.changed ? { uncertain: attachment.uncertain } : undefined}
          title={
            attachment.beside
              ? `${attachment.names.join(", ")} got off ${attachment.hostName}`
              : `${attachment.hostName} with ${attachment.names.join(", ")}`
          }
        />
      ))}
      {camera?.changed &&
        camera.values.map((cameraId) => {
          const point = points.get(cameraId);
          if (!point) return null;
          const label =
            camera.values.length > 1
              ? `One of these cameras is active after ${focus.nodeTitle} (parallel branches)`
              : `Camera after ${focus.nodeTitle}`;
          return (
            <InteractionPointPreview
              key={`camera-${cameraId}`}
              strategy={SelectedCameraPointStrategy}
              markerBorderColor={takeColorRgba}
              markerBackgroundColor={takeColorRgba}
              directionDegrees={point.direction?.x}
              directionColor={takeColorRgba}
              x={point.x}
              y={point.y}
              badgeContent=""
              badgeClassName={sequenceStateDotClassName({
                changed: true,
                uncertain: camera.values.length > 1,
              })}
              iconClassName={`${CAMERA_MARKER_CLASS} sequence-state-marker`}
              tooltipContent={label}
              title={label}
              zIndexOffset={CAMERA_MARKER_Z_INDEX_OFFSET + 1}
            />
          );
        })}
    </>
  );
}
